// JourneyAnnaChat Component
// ==========================
// Chat interface for generating learning plans from journey materials
// Feature #81 - Journey-specific Anna chat for learning plan workflow

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import {
  LearningPlanGeneratorService,
  ChatMessage,
  SuggestedLearningPlan,
  LearningPlanGenerationContext,
} from '../../services/annaTextChat';
import type { JourneyMaterial, LearningJourney, Topic } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import AnnaGlow from '../ui/AnnaGlow';
// Feature #220: Removed AnnaAvatar import - Anna's personality comes from her words, not repeated images

interface JourneyAnnaChatProps {
  /** The learning journey this chat belongs to */
  journey: LearningJourney;
  /** Uploaded materials with extracted text */
  materials: JourneyMaterial[];
  /** Optional: Existing topics when adding more material to an existing plan */
  existingTopics?: Topic[];
  /** Optional: IDs of materials that were already analyzed when plan was created (Feature #180) */
  analyzedMaterialIds?: string[];
  /** Callback when a learning plan is approved */
  onPlanApproved: (plan: SuggestedLearningPlan) => void;
  /** Optional callback to close the chat */
  onClose?: () => void;
}

// Helper function to extract first name from display name
const getFirstName = (displayName: string | null | undefined, defaultName: string): string => {
  if (!displayName) return defaultName;
  const firstName = displayName.split(' ')[0];
  return firstName || defaultName;
};

const JourneyAnnaChat: React.FC<JourneyAnnaChatProps> = ({
  journey,
  materials,
  existingTopics = [],
  analyzedMaterialIds = [],
  onPlanApproved,
  onClose,
}) => {
  // Get user context for personalized greeting
  const { user } = useAuth();
  const { t } = useTranslation('library');

  // Chat service instance
  const [chatService, setChatService] = useState<LearningPlanGeneratorService | null>(null);

  // Messages in the conversation
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Current input text
  const [inputText, setInputText] = useState('');

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Currently displayed plan (most recent from messages)
  const [currentPlan, setCurrentPlan] = useState<SuggestedLearningPlan | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInitializedRef = useRef(false);

  // Initialize chat service
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Feature #185: Pass existing topics to context for integration when adding new material
    const context: LearningPlanGenerationContext = {
      journeyId: journey.id,
      journeySubject: journey.subject || journey.title || '',
      examDate: journey.examDate,
      examFormat: journey.examFormat,
      existingTopics: existingTopics && existingTopics.length > 0
        ? existingTopics.map(tp => ({
            id: tp.id,
            title: tp.title,
            subtopics: (tp.subtopics || []).map(s => ({
              id: s.id,
              title: s.title,
              concepts: s.concepts || [],
              isCompleted: s.isCompleted || false,
            })),
            completedSessions: tp.completedSessions || 0,
            totalEstimatedSessions: tp.totalEstimatedSessions || 3,
          }))
        : undefined,
    };

    const service = new LearningPlanGeneratorService(context);
    setChatService(service);

    // Create personalized welcome message (Anna already introduced on Dashboard)
    // Feature #174: Context-aware based on whether there's an existing plan
    // Feature #180: Track which materials are new vs already analyzed
    const firstName = getFirstName(user?.displayName, t('annaChat.defaultName'));
    const hasExistingPlan = existingTopics && existingTopics.length > 0;
    const topicCount = existingTopics?.length || 0;
    const subtopicCount = existingTopics?.reduce((sum, tp) => sum + (tp.subtopics?.length || 0), 0) || 0;

    // Feature #180: Calculate new materials count (materials not yet analyzed)
    const newMaterialsCount = materials.filter(m => !analyzedMaterialIds.includes(m.id)).length;
    const totalMaterialsCount = materials.length;

    // Log context for debugging (Feature #184 prep)
    console.log('[JourneyAnnaChat] Welcome message context:', {
      hasExistingPlan,
      topicCount,
      subtopicCount,
      newMaterialsCount,
      totalMaterialsCount,
      analyzedMaterialIds: analyzedMaterialIds.length,
    });

    let welcomeContent: string;

    if (hasExistingPlan && newMaterialsCount > 0) {
      // Context: User has existing plan AND uploaded NEW materials
      console.log('[JourneyAnnaChat] Context-aware welcome: existing plan with', newMaterialsCount, 'NEW materials');
      welcomeContent = t('annaChat.journey.welcomeExistingPlanNewMaterials', {
        firstName,
        topicCount,
        topicPlural: topicCount !== 1 ? 'r' : '',
        subtopicCount,
        subtopicPlural: subtopicCount !== 1 ? 'r' : '',
        newCount: newMaterialsCount,
        newMaterialWord: newMaterialsCount === 1 ? 'nyt materiale' : 'nye materialer',
        pronoun: newMaterialsCount === 1 ? 'det' : 'dem',
        pronoun2: newMaterialsCount === 1 ? 'det' : 'de',
      });
    } else if (hasExistingPlan && newMaterialsCount === 0) {
      // Context: User has existing plan but NO new materials
      console.log('[JourneyAnnaChat] Context-aware welcome: existing plan, NO new materials');
      welcomeContent = t('annaChat.journey.welcomeExistingPlanNoMaterials', {
        firstName,
        topicCount,
        topicPlural: topicCount !== 1 ? 'r' : '',
        subtopicCount,
        subtopicPlural: subtopicCount !== 1 ? 'r' : '',
      });
    } else {
      // Context: User is creating a NEW plan (no existing topics)
      console.log('[JourneyAnnaChat] Context-aware welcome: new plan');
      welcomeContent = t('annaChat.journey.welcomeNewPlan', {
        firstName,
        subject: journey.subject,
      });
    }

    const personalizedWelcome: ChatMessage = {
      id: `welcome-${Date.now()}`,
      role: 'assistant',
      content: welcomeContent,
      timestamp: new Date(),
    };
    setMessages([personalizedWelcome]);

    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  }, [messages]);

  // Update current plan when messages change
  useEffect(() => {
    // Find the most recent message with a plan (iterate backwards)
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].suggestedPlan) {
        setCurrentPlan(messages[i].suggestedPlan ?? null);
        return;
      }
    }
  }, [messages]);

  // Generate learning plan from materials
  const handleGeneratePlan = useCallback(async () => {
    if (!chatService || isGenerating) return;

    // Get extracted text from materials
    const materialTexts = materials
      .filter(m => m.extractedText)
      .map(m => `--- ${m.fileName} ---\n${m.extractedText}`);

    if (materialTexts.length === 0) {
      setError(t('annaChat.errors.noExtractedText'));
      return;
    }

    setIsGenerating(true);
    setError(null);

    // Add a user message indicating plan generation
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: t('annaChat.journey.generatePlanFromMaterials', { count: materials.length }),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await chatService.generatePlanFromMaterials(materialTexts);
      setMessages(prev => [...prev, response]);

      if (response.suggestedPlan) {
        setCurrentPlan(response.suggestedPlan);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('annaChat.errors.unknownError');
      setError(errorMessage || t('annaChat.errors.couldNotGenerate'));
    } finally {
      setIsGenerating(false);
      inputRef.current?.focus();
    }
  }, [chatService, materials, isGenerating]);

  // Send a message to refine the plan
  const handleSendMessage = async () => {
    if (!inputText.trim() || !chatService || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatService.sendMessage(userMessage.content);
      setMessages(prev => [...prev, response]);

      if (response.suggestedPlan) {
        setCurrentPlan(response.suggestedPlan);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('annaChat.errors.unknownError');
      setError(errorMessage || t('annaChat.errors.couldNotSend'));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Approve the current plan
  const handleApprovePlan = () => {
    if (currentPlan) {
      onPlanApproved(currentPlan);
    }
  };

  // Format timestamp
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('da-DK', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Check if we have materials with text
  const hasMaterialsWithText = materials.some(m => m.extractedText);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden">
      {/* Header - Feature #220: Removed AnnaAvatar - Anna's personality comes from her words */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary-500 dark:bg-primary-700 text-white">
        <div className="flex items-center gap-3">
          <AnnaGlow size="sm" />
          <div>
            <h3 className="font-semibold">{t('annaChat.journey.headerTitle')}</h3>
            <p className="text-xs text-primary-100">{journey.subject}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            aria-label={t('annaChat.closeChat')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Material Context Bar */}
      {materials.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm text-neutral-medium">
            <span>📚</span>
            <span>
              {t('annaChat.journey.materialsUploaded', { count: materials.length })}
              {hasMaterialsWithText && (
                <span className="text-green-600 dark:text-green-400 ml-1">
                  {t('annaChat.journey.readyForAnalysis', { count: materials.filter(m => m.extractedText).length })}
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-primary-500 text-white rounded-br-md'
                  : 'bg-gray-100 dark:bg-gray-800 text-neutral-dark dark:text-gray-200 rounded-bl-md'
              }`}
            >
              {/* Message content */}
              <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                <ReactMarkdown
                  components={{
                    ul: ({ children }) => (
                      <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>
                    ),
                    li: ({ children }) => <li className="my-0">{children}</li>,
                    p: ({ children }) => <p className="my-1">{children}</p>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {/* Suggested Plan Preview */}
              {message.suggestedPlan && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-primary-800 dark:text-primary-300">
                        📋 {t('annaChat.topicPlanSuggested')}
                      </span>
                      <span className="text-xs text-primary-600 dark:text-primary-400">
                        {t('annaChat.estimatedHours', { hours: message.suggestedPlan.estimatedTotalHours })}
                      </span>
                    </div>
                    <p className="text-xs text-primary-700 dark:text-primary-400 mb-2">
                      {message.suggestedPlan.summary}
                    </p>
                    <div className="space-y-1">
                      {message.suggestedPlan.topics.map((topic, idx) => (
                        <div key={idx} className="text-xs text-primary-600 dark:text-primary-400">
                          • {topic.title}
                          {topic.subtopics && topic.subtopics.length > 0 && (
                            <span className="text-primary-400 dark:text-primary-500">
                              {' '}({t('annaChat.subtopicCount', { count: topic.subtopics.length })})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div
                className={`text-xs mt-2 ${
                  message.role === 'user' ? 'text-primary-200' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {(isLoading || isGenerating) && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-gray-500">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm">
                  {isGenerating ? t('annaChat.journey.analyzingMaterials') : t('annaChat.annaTyping')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex justify-center">
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg px-4 py-2 text-sm">
              ⚠️ {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-2">
          {!currentPlan && hasMaterialsWithText && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleGeneratePlan}
              disabled={isGenerating || isLoading}
              className="flex items-center gap-2"
            >
              ✨ {t('annaChat.generatePlan')}
            </Button>
          )}
          {currentPlan && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApprovePlan}
                disabled={isLoading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                ✅ {t('annaChat.approvePlan')}
              </Button>
              <button
                onClick={() => setInputText(t('annaChat.moreDetailPrompt'))}
                className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200"
              >
                📝 {t('annaChat.moreDetail')}
              </button>
              <button
                onClick={() => setInputText(t('annaChat.simplifyPrompt'))}
                className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-neutral-dark dark:text-gray-200"
              >
                🎯 {t('annaChat.simplify')}
              </button>
            </>
          )}
          {!hasMaterialsWithText && materials.length > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400 py-1.5">
              ⏳ {t('annaChat.journey.waitingForExtraction')}
            </span>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('annaChat.journey.inputPlaceholder')}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-neutral-dark dark:text-gray-200 placeholder:text-gray-400"
            disabled={isLoading || isGenerating}
          />
          <Button
            variant="primary"
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading || isGenerating}
            className="rounded-full px-4"
            aria-label={t('annaChat.sendMessage')}
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JourneyAnnaChat;
